// M13.7 (SKE-3420) — Push GA4 events through the loaded GTM container.
//
// Single entry point so every call site fires the same shape:
//   { event: "trafficsnapshot-<suffix>", ...params, ts: Date.now() }
//
// GTM Custom Event triggers in the GTM-5WV4CJM container fan these out
// to GA4 Event tags and (post-identify) the HubSpot tracking pixel.
// See `docs/gtm-events.md` for the SPA↔GTM contract.

// Window.dataLayer is also declared in src/client/lib/hubspot-identify.ts.
// Both declarations must use the SAME shape — TypeScript merges them and
// will throw "Subsequent property declarations must have the same type"
// otherwise. Keep the loose `Record<string, unknown>` row type in sync.
declare global {
  interface Window {
    dataLayer?: Array<Record<string, unknown>>;
  }
}

/** Closed set of event suffixes — every new event needs both a TS entry
 *  here and a matching GTM trigger / tag in the m13.7 workspace. Adding
 *  a new event without updating GTM means it'll fire into the void. */
export type TrafficSnapshotEvent =
  | "app_open"
  | "profile_connected"
  | "export_pdf_requested"
  | "export_xlsx_downloaded"
  | "digest_enabled"
  | "digest_disabled"
  | "share_link_created"
  | "conversion_events_configured"
  | "property_switched"
  // M15 (handover item 5) — share-loop + digest-forward. Matching
  // triggers/tags staged in the GTM-5WV4CJM draft workspace.
  | "share_framing_used"
  | "share_button_clicked"
  | "share_recipient_tool_cta_clicked"
  | "digest_forward_clicked"
  // MKT-745: in-app browser panel on /app/connect emailed the link.
  | "handoff_link_emailed";

/** Push a `trafficsnapshot-<suffix>` event onto window.dataLayer.
 *
 *  Safe in non-browser contexts (returns silently when window is
 *  undefined) so the helper can be imported anywhere without breaking
 *  Vitest's node environment.
 */
export function pushGtmEvent(
  suffix: TrafficSnapshotEvent,
  params: Record<string, unknown> = {},
): void {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer ?? [];
  window.dataLayer.push({
    event: `trafficsnapshot-${suffix}`,
    ...params,
    ts: Date.now(),
  });
}

/** MKT-622: Meta standard event for a first successful GA4 connect. Pushed
 *  verbatim (no `trafficsnapshot-` prefix, no `ts` key) because the GTM pixel
 *  tag and the sGTM CAPI tag both key off this exact shape; `event_id` is
 *  their shared deduplication key. Once-per-user comes from the server claim
 *  in claimSubmitApplication, not from this helper. */
export function pushSubmitApplication(eventId: string, propertyCount: number | null): void {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer ?? [];
  window.dataLayer.push({
    event: "SubmitApplication",
    event_id: eventId,
    ts_property_count: propertyCount,
  });
}

/** sessionStorage-based dedupe for events that should only fire once
 *  per browser tab session (e.g. profile_connected — the OAuth callback
 *  resolves once, but every page nav re-runs the post-bootstrap effect).
 *
 *  Returns `true` on the first call within the tab and `false` on
 *  subsequent calls. Fails open (returns `true`) when sessionStorage is
 *  disabled or throws — better to risk a duplicate event than to drop
 *  the signal entirely.
 */
export function claimSessionToken(key: string): boolean {
  if (typeof window === "undefined") return true;
  try {
    if (window.sessionStorage.getItem(key) !== null) return false;
    window.sessionStorage.setItem(key, String(Date.now()));
    return true;
  } catch {
    return true;
  }
}
